<template>
    <div>
        <v-card class="ma-4 mt-0">
            <v-card-text class="pa-0">
                <base-data-grid
                    :ref="grid.key"
                    :gridKey="grid.key"
                    :dataSource="grid.dataSource"
                    :columns="grid.columns"
                    @on-add-click="add"
                    :allowMultipleSelection="true"
                    :exportFileName="title"
                    :isStateStoringEnabled="true"
                    :allowGrouping="false"
                    :allowFilterPanel="false"
                    data-testid="content-type-grid"
                    :title="title"
                >
                    <template slot="customActionTemplates" slot-scope="row">
                        <v-tooltip top>
                            <template v-slot:activator="{ on }">
                                <a
                                    v-on="on"
                                    @click.prevent="editContentTypeRights(row.data.item)"
                                    class="px-2"
                                >
                                    <v-icon color="blue">mdi-security</v-icon>
                                </a>
                            </template>
                            <span>{{ $t("ContentTypeAccessRights") }}</span>
                        </v-tooltip>
                        <v-tooltip top>
                            <template v-slot:activator="{ on }">
                                <a
                                    v-on="on"
                                    @click.prevent="editExportConfiguration(row.data.item)"
                                    class="px-2"
                                >
                                    <v-icon color="blue-grey">mdi-cog-transfer-outline</v-icon>
                                </a>
                            </template>
                            <span>{{ $t("ExportConfiguration") }}</span>
                        </v-tooltip>
                        <v-tooltip top>
                            <template v-slot:activator="{ on }">
                                <a
                                    v-on="on"
                                    @click.prevent="editAlerts(row.data.item)"
                                    class="px-2"
                                >
                                    <v-icon color="warning">mdi-bell</v-icon>
                                </a>
                            </template>
                            <span>{{ $t("Alerts") }}</span>
                        </v-tooltip>
                        <v-tooltip top>
                            <template v-slot:activator="{ on }">
                                <a
                                    v-on="on"
                                    @click.prevent="editFieldsRights(row.data.item)"
                                    class="px-2"
                                >
                                    <v-icon color="blue">mdi-server-security</v-icon>
                                </a>
                            </template>
                            <span>{{ $t("FieldsAccessRights") }}</span>
                        </v-tooltip>
                        <v-tooltip top>
                            <template v-slot:activator="{ on }">
                                <a
                                    v-on="on"
                                    class="px-2"
                                    @click="canEditAutoimportSettings(row.data.item) && editAutoImportConfigs(row.data.item)"
                                >
                                    <v-icon :color="canEditAutoimportSettings(row.data.item) ? 'blue' : 'grey'">mdi-import</v-icon>
                                </a>
                            </template>
                            <span v-if="canEditAutoimportSettings(row.data.item)">{{ $t("AutoImport") }}</span>
                            <span v-if="!canViewAi">{{ $t("NoAILicenseAccess") }}</span>
                            <span v-if="!row.data.item.AutoimportEnabled && canViewAi">{{ $t("Site.ContentType.AutoImportDisabledMessage") }}</span>
                        </v-tooltip>
                        <v-tooltip top>
                            <template v-slot:activator="{ on }">
                                <a
                                    v-on="on"
                                    class="px-2"
                                    @click="canViewAi && openDocumentIntelligenceSettings(row.data.item)"
                                >
                                    <v-icon :color="canViewAi ? 'blue' : 'grey'">mdi-brain</v-icon>
                                </a>
                            </template>
                            <span>{{ canViewAi ? $t("IntelligentFields") : $t("NoAILicenseAccess") }}</span>
                        </v-tooltip>
                        <v-tooltip top>
                            <template v-slot:activator="{ on }">
                                <a
                                    v-on="on"
                                    @click.prevent="editItem(row.data.item)"
                                    class="px-2"
                                >
                                    <v-icon color="primary">mdi-pencil</v-icon>
                                </a>
                            </template>
                            <span>{{ $t("Edit") }}</span>
                        </v-tooltip>
                        <v-tooltip top>
                            <template v-slot:activator="{ on }">
                                <a
                                    v-on="on"
                                    @click.prevent="deleteItem(row.data.item)"
                                    class="px-2"
                                >
                                    <v-icon color="red">mdi-delete</v-icon>
                                </a>
                            </template>
                            <span>{{ $t("Delete") }}</span>
                        </v-tooltip>
                    </template>
                    <template v-slot:action-buttons>
                        <v-btn small color="primary" @click.prevent="onImport">
                            {{ $t("Import") }}
                            <v-icon>mdi-application-import</v-icon>
                        </v-btn>
                        <v-btn small color="primary" @click.prevent="onExport">
                            {{ $t("Export") }}
                            <v-icon>mdi-application-export</v-icon>
                        </v-btn>
                    </template>
                </base-data-grid>
            </v-card-text>
        </v-card>
        <ContentTypeImportModal
            :ref="contentTypeImportModalKey"
            @on-imported="onItemSaved"
        />
    </div>
</template>

<script>
import ContentTypeService from '@/services/ContentTypeService';
import FieldsRights from '@/views/administration/contentType/fieldRights/FieldsRights.vue';
import ContentTypeImportModal from '@/components/administration/contentType/ContentTypeImportModal.vue';
import AutoImportConfig from '@/views/administration/contentType/autoimport/ContentTypeAutoImportConfig.vue';
import Utils from '@/ecm/common/Utils';
import DocumentIntelligenceProvidersSettings from '@/views/administration/contentType/documentIntelligence/DocumentIntelligenceListProvidersSettings.vue';
import ContentType from '@/views/administration/contentType/ContentType.vue';
import AccessRightsAdministration from '@/views/administration/contentType/rights/AccessRightsAdministration.vue';
import Alerts from '@/views/administration/contentType/alerts/Alerts.vue';
import ExportConfig from '@/views/administration/contentType/exportConfig/ExportConfig.vue';

export default {
    components: {
        FieldsRights,
        ContentTypeImportModal,
        AutoImportConfig,
        DocumentIntelligenceProvidersSettings,
        ContentType,
        AccessRightsAdministration
    },
    data() {
        return {
            title: this.$t('ContentTypes'),
            grid: {
                key: 'contentTypeGrid',
                dataSource: ContentTypeService.getGrid(),
                columns: [
                    {
                        field: 'Name',
                        caption: this.$t('Name'),
                        type: 'string',
                    },
                    {
                        field: 'Version',
                        caption: this.$t('Version'),
                        type: 'string',
                    },
                ],
            },
            childComponents: [],
            contentTypeRightsModalKey: 'contentTypeRightsModal',
            contentTypeImportModalKey: 'contentTypeImportModal',
            documentIntelligenceProvidersSettingsKey: 'documentIntelligenceProvidersSettings',
            contentTypeKey: 'contentType',
        };
    },
    computed: {
        gridInstance() {
            return this.$refs[this.grid.key];
        },
        canViewAi() {
            return this.$store.getters['account/userMenu'].CanViewAI;
        },
    },
    beforeRouteEnter(to, from, next) {
        next((vm) => {
            vm.childComponents = [
                ContentType.name,
                DocumentIntelligenceProvidersSettings.name,
                AutoImportConfig.name,
                FieldsRights.name,
                AccessRightsAdministration.name,
                ExportConfig.name,
                Alerts.name
            ];
            if (!vm.childComponents.includes(from.name)) {
                vm.gridInstance.saveState(null);
            }
        });
    },
    beforeRouteLeave(to, from, next) {
        this.$nextTick(() => {
            if (this.childComponents.includes(to.name)) {
                const gridState = this.gridInstance.loadState();
                this.gridInstance.saveState(gridState);
            }
            next();
        });
    },
    methods: {
        canEditAutoimportSettings(item) {
            return this.canViewAi && item.AutoimportEnabled;
        },
        editContentTypeRights(dto) {
            this.$router.push(`/administration/content-types/access-rights/${dto.Id}`);
        },
        editExportConfiguration(dto) {
            this.$router.push(`/administration/content-types/export-config/${dto.Id}`);
        },
        editAlerts(dto) {
            this.$router.push(`/administration/content-types/alerts/${dto.Id}`);
        },
        editFieldsRights(dto) {
            this.$router.push(`/administration/content-types/access-rights-fields/${dto.Id}`);
        },
        editAutoImportConfigs(dto) {
            this.$router.push(`/administration/content-types/auto-import-configuration/${dto.Id}`);
        },
        openDocumentIntelligenceSettings(dto) {
            this.$router.push(`/administration/content-type/document-intelligence-settings/${dto.Id}`);
        },
        onImport() {
            this.$refs[this.contentTypeImportModalKey].new();
        },
        editItem(dto) {
            this.$router.push(`/administration/content-type/${dto.Id}`);
        },
        deleteItem(dto) {
            this.$confirm(this.$t('ItemDeleteConfirmation'), {
                title: this.$t('Delete'),
                icon: 'mdi-alert',
                color: 'red',
                buttonTrueText: this.$t('Delete'),
                buttonFalseText: this.$t('Cancel'),
            }).then((res) => {
                if (res === 1) {
                    ContentTypeService.delete(dto.Id).then((result) => {
                        if (result) {
                            this.gridInstance.refresh();
                            Utils.toast.success(
                                this.$t('ContentType') + ' ' + this.$t('DeletedSuccessfully')
                            );
                            ContentTypeService.clearCacheForContentTypeDeletion(dto.Id);

                            this.$store.dispatch('account/getUserContentTypes');
                            this.$router.push({
                                name: 'ContentTypeList',
                            });
                        }
                    });
                }
            });
        },
        add() {
            this.$router.push('/administration/content-type');
        },
        onItemSaved() {
            this.$store.dispatch('account/getUserContentTypes');
            this.gridInstance.refresh();
        },
        onExport() {
            const selectedItems = this.gridInstance.selectedRowKeys;
            if (selectedItems.length === 0) {
                Utils.toast.warning(this.$t('ItemsSelectionError'));
                return;
            }
            ContentTypeService.export(selectedItems);
        },
    },
};
</script>
